import CryptoJS from 'crypto-js';

let serverTimeOffset = 0;
let isRenewingIpInfo = false;

export const appName = process.env.REACT_APP_NAME || 'invoice-payment';

/**
 * Returns encrypted x-from-app-name header for /ipinfo
 */
export const getXAppName = () => {
  const encrypted = CryptoJS.AES.encrypt(appName, window.location.hostname).toString();
  return encrypted;
};

/**
 * Reads cookie with given name
 */
export function getCookie(name: string): string | undefined {
  /* eslint-disable-next-line no-useless-escape */
  const matches = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
  );
  return matches ? decodeURIComponent(matches[1]) : undefined;
}

/**
 * Converts FormData into sorted JSON string for HMAC signing
 */
export function getFormDataPayload(formData: FormData): string {
  const obj: Record<string, string> = {};
  const keys = Array.from(formData.keys()).sort();

  for (const key of keys) {
    const value = formData.get(key);
    if (typeof value === 'string') {
      obj[key] = value.replace(/\r\n/g, '\n');
    }
  }

  return JSON.stringify(obj);
}

/**
 * Generate HMAC signature for Proxy V2
 */
export async function generateSignature(
  method: string,
  path: string,
  body: string,
  timestamp: number
): Promise<string> {
  const sessionToken = getCookie('__cf_sid');
  if (!sessionToken) {
    console.warn('Proxy session cookie not found');
    return '';
  }

  const encoder = new TextEncoder();
  const keyData = encoder.encode(sessionToken);
  const payload = `${method.toUpperCase()}${path}${body}${timestamp}`;

  const key = await crypto.subtle.importKey(
    'raw',
    keyData,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(payload));

  return btoa(String.fromCharCode(...new Uint8Array(signature)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Calls /ipinfo and updates serverTimeOffset
 */
export const fetchIpInfo = async () => {
  try {
    const res = await fetch('/ipinfo', {
      headers: {
        'Content-Type': 'application/json',
        'x-from-app-name': getXAppName(),
      },
      credentials: 'include',
    });
    if (res.ok) {
      const data = await res.json();
      if (data.server_ts) {
        serverTimeOffset = data.server_ts - Math.floor(Date.now() / 1000);
      }
      return data;
    }
  } catch (err) {
    console.warn('Failed to fetch /ipinfo', err);
  }
  return null;
};

/**
 * Checks JWT active time in __cf_sid cookie.
 * If remaining time < 30 minutes, calls /ipinfo to renew cookie.
 */
export const checkAndRenewIpInfo = async () => {
  const token = getCookie('__cf_sid');
  if (!token) return;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) return;

    // Decode payload JWT
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    if (payload && payload.exp) {
      const now = Math.floor(Date.now() / 1000);
      const bufferSeconds = 1800; // 30 mins

      if (payload.exp - now < bufferSeconds) {
        if (isRenewingIpInfo) return;
        isRenewingIpInfo = true;

        try {
          await fetchIpInfo();
        } finally {
          isRenewingIpInfo = false;
        }
      }
    }
  } catch (e) {
    // Ignore parse error
  }
};

/**
 * Custom fetch wrapper for Proxy V2 HMAC Security
 */
export const customFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  try {
    await checkAndRenewIpInfo();

    const requestUrl = typeof input === 'string' ? input : input.url;

    // Resolve full URL to extract path & search
    const baseURL = window.location.origin;
    const fullUrl = requestUrl.startsWith('http')
      ? requestUrl
      : new URL(requestUrl, baseURL).toString();
    const urlObj = new URL(fullUrl);

    // Skip HMAC for /ipinfo or external non-proxy domains
    const isIpInfo = requestUrl.includes('/ipinfo');
    const isExternalDomain =
      !requestUrl.includes(window.location.hostname) && requestUrl.startsWith('http');

    if (isIpInfo || isExternalDomain) {
      return fetch(input, {
        ...init,
        credentials: 'include',
      });
    }

    const method = (init?.method || 'GET').toUpperCase();
    const path = urlObj.pathname + urlObj.search;

    let body = '';
    if (init?.body) {
      if (init.body instanceof FormData) {
        body = getFormDataPayload(init.body);
      } else if (typeof init.body === 'string') {
        body = init.body;
      } else {
        body = JSON.stringify(init.body);
      }
    }

    const ts = Math.floor(Date.now() / 1000) + serverTimeOffset;
    const signature = await generateSignature(method, path, body, ts);

    const headers = new Headers(init?.headers || {});
    headers.set('x-signature', signature);
    headers.set('x-timestamp', ts.toString());

    return fetch(input, {
      ...init,
      headers,
      credentials: 'include',
    });
  } catch (error) {
    console.error('Error in customFetch HMAC wrapper:', error);
    return fetch(input, {
      ...init,
      credentials: 'include',
    });
  }
};
